/**
 * Mappa mondiale dell'archivio /data-index.
 *
 * Il server ha già deciso quali paesi sono coperti e li ha marcati nel DOM:
 * questo file aggiunge solo zoom, tooltip e ricerca. Se non parte, restano una
 * mappa statica corretta e una lista di link funzionante.
 */
import {
  MIN_SCALE,
  MAX_SCALE,
  clampView,
  fillUnit,
  visibleWorld,
  viewForBounds,
  DEFAULT_PADDING,
  expandBounds,
  projectToScreen,
  normalizeWheelDeltaY,
  wheelZoomFactor,
} from './factbookMap/viewport';
import { matchesFilters, filterCountries, findBestMatch } from './factbookMap/countryIndex';
import { initContinentDropdown } from './factbookMap/dropdown';

const RESET_VIEW = { k: MIN_SCALE, x: 0, y: 0 };
// Fattore per click dei bottoni +/-. Era 1,5 (5 click per andare da 1x a
// 6x, ogni passo un salto grosso); 1,3 da circa sette passi piu' graduali
// sull'intera escursione di zoom.
const ZOOM_BUTTON_FACTOR = 1.3;
// Durata del suggerimento "usa Ctrl/Cmd + rotella": abbastanza per essere
// letto, non cosi' lunga da infastidire chi sta solo scorrendo la pagina.
const WHEEL_HINT_DURATION = 1500;
const DOT_RADIUS = 4.5;
const HIT_RADIUS = 10;

// Stacco fra il punto ancorato e il box del tooltip, in pixel schermo. Questo
// e' l'originale del valore: il CSS lo legge dalla custom property
// `--tip-gap`, che scriviamo noi sull'elemento all'avvio. Serve anche qui
// perche' e' con questa misura che si decide se sopra il punto c'e' spazio
// per il box e di quanto rientrare per non uscire dal riquadro.
const TIP_GAP = 16;

// Aria fra il box del tooltip e i bordi del riquadro. Il canvas ha
// `overflow: hidden`: quello che esce non viene solo nascosto, diventa
// irraggiungibile col mouse, CTA compresa.
const TIP_MARGIN = 8;
// Spessore del contorno dei pallini a scala 1, controscalato in render():
// stesso valore che copriva gia' `.factbook-map__dot` in CSS, cosi' l'aspetto
// a zoom 1 non cambia rispetto a prima di questa correzione.
const DOT_STROKE_WIDTH = 1;

// Sotto questa distanza (in pixel schermo) un pointerdown+pointerup è un tap,
// non un trascinamento: senza soglia un tap che trema di 1-2px per l'inerzia
// del dito verrebbe scambiato per drag e la navigazione al tap si romperebbe.
const DRAG_THRESHOLD = 4;

// Sotto questo valore un singolo segmento di pointermove e' rumore del
// sensore (jitter di trackpad o mouse), non movimento reale: senza questa
// zona morta, restare fermi per un istante puo' comunque accumulare oltre
// DRAG_THRESHOLD e mangiarsi un click legittimo.
const PAN_NOISE_FLOOR = 1;

function readPayload(root) {
  const node = root.querySelector('[data-factbook-map-payload]');

  if (!node) {
    return null;
  }

  try {
    return JSON.parse(node.textContent);
  } catch (error) {
    return null;
  }
}

/**
 * Distanza euclidea fra due punti schermo {x, y}.
 * Math.sqrt(dx*dx + dy*dy) e non Math.hypot: risultato identico, e il resto
 * del file (vedi il segmento di drag in pointermove) fa gia' lo stesso calcolo
 * per esteso. Nessun motivo di introdurre una seconda forma per lo stesso
 * conto in codice che gia' funziona.
 */
function distanceBetween(a, b) {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Vincola `value` all'intervallo [min, max]. Se l'intervallo e' degenere
 * (max < min, cioe' un box piu' largo del riquadro che dovrebbe contenerlo)
 * vince `max`: meglio sbordare da un lato solo che da tutti e due.
 */
function clampBetween(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/**
 * Rilevamento piattaforma volutamente semplice e robusto: serve solo a
 * scegliere fra "Ctrl" e "Cmd" nel testo del suggerimento della rotella, non
 * a diramare alcuna logica di funzionamento (lo zoom da tastiera legge
 * ctrlKey/metaKey a prescindere da questo). `userAgentData.platform` e'
 * preferito quando disponibile (Chromium); `navigator.platform` e' deprecato
 * ma ancora universalmente supportato; `userAgent` e' l'ultima spiaggia.
 */
function isApplePlatform() {
  const source = (window.navigator.userAgentData && window.navigator.userAgentData.platform)
    || window.navigator.platform
    || window.navigator.userAgent
    || '';

  return /mac|iphone|ipad|ipod/i.test(source);
}

export function initFactbookMap(root) {
  const scope = root || document;
  const container = scope.querySelector('[data-factbook-map]');

  if (!container) {
    return null;
  }

  const payload = readPayload(container);

  if (!payload) {
    return null;
  }

  const canvas = container.querySelector('[data-factbook-map-canvas]');
  const layer = container.querySelector('[data-factbook-map-layer]');
  const tip = container.querySelector('[data-factbook-map-tip]');
  // Il box e il link non sono piu' lo stesso nodo: `tip` e' il contenitore
  // posizionato (e quello che il resto del file misura e sposta), `tipLink` e'
  // l'<a> che ci sta dentro e a cui va scritto l'href. Vedi il partial per il
  // perche' della ✕ fratello invece che annidata.
  const tipLink = container.querySelector('[data-factbook-map-tip-link]');
  const tipClose = container.querySelector('[data-factbook-map-tip-close]');
  const tipName = container.querySelector('[data-tip-name]');
  const tipLanguage = container.querySelector('[data-tip-language]');
  const tipCta = container.querySelector('[data-factbook-map-cta]');
  const select = container.querySelector('[data-factbook-map-continent]');
  const search = container.querySelector('[data-factbook-map-search]');
  const list = document.querySelector('[data-factbook-map-list]');
  const empty = document.querySelector('[data-factbook-map-empty]');

  // Il canvas manca solo se la geometria non era leggibile in PHP (vedi
  // partials/factbook-map.blade.php, `$has_geometry`): in quel caso il server
  // renderizza già solo il finder e la lista, senza il div della mappa. Senza
  // questa uscita, i successivi setAttribute su `layer`/`tip` andrebbero in
  // eccezione al primo cambio di continente o ricerca, anche se la lista
  // sottostante è perfettamente funzionante e non deve essere trascinata giù.
  if (!canvas || !layer || !tip || !tipLink || !tipClose || !tipName || !tipLanguage || !tipCta) {
    return null;
  }

  // Contratto col Task 4: senza questa classe `touch-action: none` e
  // `cursor: grab` restano spenti in CSS, e la mappa resta un'immagine
  // scrollabile invece che un riquadro con pan JS. Va aggiunta qui, dopo aver
  // verificato che il canvas esiste davvero, non prima.
  canvas.classList.add('is-interactive');

  // Modalita' "cooperativa" (la mappa compatta in home): la mappa sta a meta'
  // di una pagina lunga, quindi il dito che scorre deve scorrere la pagina,
  // non restare intrappolato nel riquadro. Un dito in verticale scorre la
  // pagina (touch-action: pan-y in CSS), un dito in orizzontale sposta la
  // mappa, due dita la zoomano e la spostano. E' lo stesso principio gia'
  // applicato alla rotella qui sotto (senza Ctrl/⌘ scorre la pagina). Su
  // /data-index la mappa e' il contenuto della pagina e resta com'era.
  const cooperative = container.hasAttribute('data-factbook-map-cooperative');

  // Lo stacco del tooltip vive in JavaScript (TIP_GAP) e da qui arriva al CSS,
  // che lo usa per la transform e per il ponte sopra lo stacco stesso. Un
  // secondo valore scritto a mano nel foglio di stile sarebbe destinato a
  // divergere da quello con cui qui si decide il ribaltamento.
  tip.style.setProperty('--tip-gap', `${TIP_GAP}px`);

  const viewBox = payload.viewBox.split(' ').map(Number);
  const world = { width: viewBox[2], height: viewBox[3] };
  const byIso = {};
  payload.countries.forEach((country) => { byIso[country.iso] = country; });

  // Il sottoalbero SVG e' statico dopo il render del server: nessuno di
  // questi nodi viene aggiunto o rimosso da questo script. Vanno raccolti una
  // sola volta, non ad ogni frame di trascinamento (erano 3 querySelectorAll
  // su ~350 nodi ripetuti ad ogni pointermove/wheel). I poligoni dei paesi non
  // sono in questo elenco: la loro stroke-width non viene piu' contro-scalata
  // via JS (vedi il commento in render() qui sotto), quindi non serve
  // raccoglierli qui.
  const dotEls = Array.from(layer.querySelectorAll('.factbook-map__dot'));
  const hitEls = Array.from(layer.querySelectorAll('.factbook-map__hit'));

  // Governa SOLO l'apertura del tooltip al passaggio del puntatore: su un
  // dispositivo senza hover quei listener non avrebbero nulla da ascoltare.
  // Non decide piu' che cosa fa un tocco — quello dipende dal singolo gesto e
  // si legge da `event.pointerType` (vedi pressedPointerType), perche' un
  // portatile con schermo touch riporta `hover: hover` e riceve comunque tocchi.
  const canHover = window.matchMedia('(hover: hover)').matches;

  let view = { k: MIN_SCALE, x: 0, y: 0 };
  let activeIso = null;

  // Geometria del canvas rispetto al mondo SVG: cambia solo al resize della
  // finestra, non ad ogni pointermove/wheel. Misurarla di nuovo ad ogni frame
  // significava un getBoundingClientRect() (reflow forzato) e un ricalcolo di
  // `unit` ad ogni singolo movimento, anche se il riquadro non si era mosso.
  let canvasBox = canvas.getBoundingClientRect();
  let canvasUnit = 1;
  let canvasOffsetX = 0;
  let canvasOffsetY = 0;
  // Quanto mondo entra davvero nel riquadro, in unita' mondo. Su desktop e' il
  // mondo intero; sotto il breakpoint tablet il riquadro ha proporzioni
  // diverse dalla mappa e lo slice ne ritaglia i lati, quindi qui c'e' meno di
  // `world`. E' la misura che decide quanto pan e' lecito (vedi setView).
  let canvasVisible = world;

  function measureCanvas() {
    canvasBox = canvas.getBoundingClientRect();
    canvasUnit = fillUnit(canvasBox, world);
    canvasOffsetX = (canvasBox.width - world.width * canvasUnit) / 2;
    canvasOffsetY = (canvasBox.height - world.height * canvasUnit) / 2;
    canvasVisible = visibleWorld(canvasBox, world);
  }

  measureCanvas();

  // Isolamento per puntatore: con una sola voce condivisa, un secondo dito
  // che scende durante un trascinamento sovrascriveva lo stato del primo.
  // Oggetto semplice e non Map: le chiavi sono sempre pointerId (numeri, mai
  // collisioni con proprieta' ereditate) e le operazioni che servono qui
  // (Object.keys, delete) bastano; introdurre Map non aggiungerebbe nulla a
  // codice che gia' funziona. L'oggetto pointerId -> posizione e' la fonte di
  // verita' su quante dita sono attive; pan a 1 dito e pinch a 2 leggono da
  // qui.
  const activePointers = {};

  function activePointerCount() {
    return Object.keys(activePointers).length;
  }

  function activePointerList() {
    return Object.keys(activePointers).map((id) => activePointers[id]);
  }
  let dragFrom = null;
  let dragDistance = 0;
  // Il click sintetico che il browser genera dopo un pointerup arriva DOPO
  // endDrag(): la soglia va quindi salvata qui e consumata dal listener
  // 'click', perché a quel punto `dragFrom` è già stato azzerato.
  let suppressNextClick = false;
  // Paese sotto il puntatore all'inizio del gesto, letto in pointerdown e
  // consumato dal listener 'click': vedi li' il perche' non si possa leggerlo
  // dal bersaglio del click.
  let pressedIso = null;
  // Che cosa ha iniziato il gesto: 'mouse', 'touch' o 'pen'. Va letto in
  // pointerdown perche' l'evento 'click' che ne segue non lo riporta, ed e'
  // quello che decide se il tocco apre il tooltip o naviga (vedi
  // navigateFromEvent). Deliberatamente NON `matchMedia('(hover: hover)')`:
  // quella dice come e' fatto il dispositivo, non come lo si sta usando in
  // questo istante — un portatile con schermo touch riporta `hover: hover` e
  // il suo utente tocca lo schermo lo stesso, e i due input convivono sulla
  // stessa pagina.
  let pressedPointerType = 'mouse';
  // Riferimento del pinch: distanza fra le due dita e scala della vista nel
  // momento in cui il secondo dito e' sceso. Il fattore corrente/iniziale
  // applicato a quella scala e' il nuovo livello di zoom.
  let pinch = null;

  function anchorFor(iso) {
    return payload.centroids[iso] || payload.markers[iso] || null;
  }

  // Ancore dei paesi raggruppate per continente, per allargare il rettangolo
  // dell'inquadratura fino a comprenderli tutti (vedi expandBounds). Costruito
  // una volta sola: l'elenco dei paesi non cambia dopo il render del server, e
  // rifarlo ad ogni cambio di continente sarebbe lavoro ripetuto a ogni scelta
  // del filtro.
  const anchorsByContinent = {};

  payload.countries.forEach((country) => {
    const anchor = anchorFor(country.iso);

    if (!country.continent || !anchor) {
      return;
    }

    if (!anchorsByContinent[country.continent]) {
      anchorsByContinent[country.continent] = [];
    }

    anchorsByContinent[country.continent].push(anchor);
  });

  // Ingombro del box, misurato e non ricalcolato ad ogni fotogramma: leggere
  // offsetWidth/offsetHeight subito dopo aver scritto uno stile forza il
  // browser a rifare il layout, e placeTip() gira anche ad ogni pointermove di
  // un trascinamento. Cambia solo col testo dentro al box (showTip) o col
  // riquadro (resize), quindi si rimisura li'.
  let tipSize = { width: 0, height: 0 };

  function measureTip() {
    tipSize = { width: tip.offsetWidth, height: tip.offsetHeight };
  }

  /**
   * Posiziona il tooltip sul punto ancorato tenendolo INTERO dentro al
   * riquadro. Due cose, entrambe necessarie perche' la CTA sia raggiungibile:
   *
   * - se sopra il punto non ci sta, il box si ribalta sotto invece di uscire
   *   dal bordo alto (il canvas ha overflow: hidden, quindi "fuori" significa
   *   tagliato e non cliccabile);
   * - left/top vengono comunque vincolati ai bordi. Serve quando il punto
   *   ancorato e' fuori dall'inquadratura ma il paese si vede lo stesso: con
   *   Europe selezionato la Russia si vede a ovest, ma il suo centroide sta in
   *   Siberia e proietta oltre il bordo destro del riquadro. Senza vincolo il
   *   box finiva fuori dal canvas con la sua CTA.
   *
   * Il vincolo agisce sul punto e non sul box perche' la trasformazione CSS
   * fra i due e' una traslazione fissa (translate(-50%, -100%) piu' lo stacco):
   * spostare l'uno sposta l'altro della stessa quantita'.
   */
  function placeTip() {
    const anchor = activeIso ? anchorFor(activeIso) : null;

    if (!anchor) {
      return;
    }

    const point = projectToScreen(anchor, view, world, canvasBox);
    const half = tipSize.width / 2;
    const below = point.y - TIP_GAP - tipSize.height < TIP_MARGIN;

    tip.classList.toggle('factbook-map__tip--below', below);

    // Estremi entro cui il punto puo' stare perche' il box, tradotto dalla
    // sua transform, resti dentro al riquadro con TIP_MARGIN di aria.
    const minY = below
      ? TIP_MARGIN - TIP_GAP
      : TIP_MARGIN + TIP_GAP + tipSize.height;
    const maxY = below
      ? canvasBox.height - TIP_MARGIN - TIP_GAP - tipSize.height
      : canvasBox.height - TIP_MARGIN + TIP_GAP;

    tip.style.left = `${clampBetween(point.x, half + TIP_MARGIN, canvasBox.width - half - TIP_MARGIN)}px`;
    tip.style.top = `${clampBetween(point.y, minY, maxY)}px`;
  }

  function render() {
    layer.setAttribute(
      'transform',
      `translate(${world.width / 2} ${world.height / 2}) scale(${view.k}) ` +
      `translate(${-world.width / 2 + view.x} ${-world.height / 2 + view.y})`,
    );

    // Pallini: raggio e contorno mantengono dimensione costante sullo schermo
    // (contro-scala). A 6x il raggio e' 0,75 unita' mondo; senza contro-scalare
    // anche il contorno, uno stroke-width fisso di 1 unita' (centrato sul
    // bordo del cerchio) trasforma il pallino in un anello con un nucleo
    // minuscolo, proprio ai livelli di zoom per cui i marker esistono.
    //
    // I bordi dei paesi NON sono contro-scalati, deliberatamente: un attributo
    // di presentazione SVG ha specificita' zero e perde sempre contro una
    // regola CSS, e `stroke-width: 0.4` su `.factbook-map__country` (vedi
    // factbook-map.scss) vince comunque. Impostarlo qui sarebbe stato codice
    // morto che promette una contro-scala che non arriva mai allo schermo: lo
    // spessore del bordo scala quindi con lo zoom, fino a circa 2,4px a 6x.
    dotEls.forEach((dot) => {
      dot.setAttribute('r', DOT_RADIUS / view.k);
      dot.setAttribute('stroke-width', DOT_STROKE_WIDTH / view.k);
    });
    hitEls.forEach((hit) => { hit.setAttribute('r', HIT_RADIUS / view.k); });

    placeTip();
  }

  function setView(next) {
    // `canvasVisible` e non `world`: e' cosi' che il pan resta possibile a
    // scala 1 quando il riquadro ritaglia la mappa ai lati (mobile). Vedi
    // clampView in viewport.js per il perche' i due non coincidono.
    view = clampView(next, world, canvasVisible);
    render();
  }

  /**
   * Zooma alla scala `k` mantenendo fermo, sullo schermo, il punto mondo che
   * si trova sotto (screenX, screenY) nella vista attuale. E' cosi' che un
   * pinch ingrandisce dove le dita stanno guardando invece che al centro del
   * riquadro (che e' invece il comportamento, invariato, dei bottoni +/- e
   * della rotella).
   *
   * Derivazione: da projectToScreen, screenX = offsetX + (W/2 + (px - W/2 +
   * view.x) * k) * unit. Isolando "px - W/2 + view.x" si ottiene una costante
   * C = (screenX - offsetX) / unit - W/2 che non dipende da k (il riquadro
   * non si muove durante uno zoom). Imponendo che lo stesso punto mondo
   * proietti ancora su screenX dopo il cambio di scala si arriva a:
   *   x_nuovo = x_attuale + C * (1/k_nuovo - 1/k_attuale)
   * (stessa cosa sull'asse y con l'altezza del mondo).
   */
  function zoomAroundPoint(screenX, screenY, k) {
    // Clampato qui e non lasciato a setView/clampView: la formula sotto usa
    // `k` sia nel calcolo di x/y sia nell'assegnazione finale, quindi se
    // clampView cambiasse silenziosamente il valore applicato (oltre 6x o
    // sotto 1x) l'ancoraggio al punto delle dita andrebbe alla deriva vicino
    // ai limiti.
    const clampedK = Math.min(MAX_SCALE, Math.max(MIN_SCALE, k));
    const focusX = (screenX - canvasOffsetX) / canvasUnit - world.width / 2;
    const focusY = (screenY - canvasOffsetY) / canvasUnit - world.height / 2;

    setView({
      k: clampedK,
      x: view.x + focusX * (1 / clampedK - 1 / view.k),
      y: view.y + focusY * (1 / clampedK - 1 / view.k),
    });
  }

  function showTip(iso) {
    const country = byIso[iso];

    if (!country) {
      return;
    }

    activeIso = iso;
    tipName.textContent = country.name;
    tipLanguage.textContent = country.language;
    // Destinazione del box, che e' un <a> (vedi il commento nel partial): e'
    // questo che rende il Cmd/Ctrl+click e il tasto centrale gesti veri invece
    // di una navigazione forzata nella scheda corrente. Va riscritto ad ogni
    // apertura, perche' il box e' uno solo per tutti i paesi.
    tipLink.href = country.url;
    tip.classList.add('is-visible');

    Array.from(layer.querySelectorAll('.is-active')).forEach((node) => node.classList.remove('is-active'));
    Array.from(layer.querySelectorAll(`[data-iso="${iso}"]`)).forEach((node) => node.classList.add('is-active'));

    // Il nome appena scritto cambia la larghezza del box, ed e' su quella che
    // placeTip() decide rientri e ribaltamento: misurare dopo il testo, mai
    // prima.
    measureTip();
    placeTip();
  }

  function hideTip() {
    activeIso = null;
    tip.classList.remove('is-visible');
    Array.from(layer.querySelectorAll('.is-active')).forEach((node) => node.classList.remove('is-active'));
  }

  function isoFrom(event) {
    return event.target && event.target.dataset ? event.target.dataset.iso : null;
  }

  // --- zoom ---------------------------------------------------------------

  Array.from(container.querySelectorAll('[data-factbook-map-zoom]')).forEach((button) => {
    button.addEventListener('click', () => {
      const factor = button.dataset.factbookMapZoom === 'in' ? ZOOM_BUTTON_FACTOR : 1 / ZOOM_BUTTON_FACTOR;
      setView({ k: view.k * factor, x: view.x, y: view.y });
    });
  });

  // Creato qui via JS e non nel markup server-side: e' puro arricchimento
  // dell'interazione con la rotella, inutile senza JavaScript (a differenza
  // della lista sotto la mappa, che resta l'equivalente accessibile anche a
  // bundle non caricato). Tenerlo fuori da PHP/Blade evita di aggiungere
  // markup che esisterebbe anche quando lo script non parte.
  const wheelHint = document.createElement('div');
  wheelHint.className = 'factbook-map__wheel-hint';
  wheelHint.setAttribute('aria-hidden', 'true');
  // "Hold ... and scroll" e non "Use ... + scroll": scritto col piu', il
  // suggerimento si legge come la scorciatoia da tastiera ⌘+ / Ctrl+, che nel
  // browser ingrandisce la pagina. Chi lo ha letto cosi' ha premuto quella, ha
  // visto ingrandirsi il sito invece della mappa, e ha dato per rotto lo zoom.
  wheelHint.textContent = isApplePlatform() ? 'Hold ⌘ and scroll to zoom' : 'Hold Ctrl and scroll to zoom';
  canvas.appendChild(wheelHint);

  let wheelHintTimer = null;

  const wheelHintText = wheelHint.textContent;

  function showWheelHint(text) {
    wheelHint.textContent = text || wheelHintText;
    wheelHint.classList.add('is-visible');

    // Se l'utente rifa' la rotella mentre il messaggio e' ancora visibile, il
    // conteggio riparte da capo invece di accavallare timer: senza questa
    // pulizia il primo timeout potrebbe far svanire il messaggio proprio
    // mentre la rotella e' ancora in uso.
    if (wheelHintTimer) {
      clearTimeout(wheelHintTimer);
    }

    wheelHintTimer = setTimeout(() => {
      wheelHint.classList.remove('is-visible');
      wheelHintTimer = null;
    }, WHEEL_HINT_DURATION);
  }

  canvas.addEventListener('wheel', (event) => {
    // Rotella normale: NON zooma e NON ruba lo scroll della pagina (stesso
    // comportamento di Google Maps e Figma). Il pinch a due dita su trackpad
    // Mac genera pero' eventi wheel con ctrlKey a true anche senza che
    // l'utente tocchi il tasto Ctrl: e' cosi' che il pinch da trackpad ottiene
    // lo zoom "gratis" nel ramo sotto, senza che compaia il suggerimento.
    if (!event.ctrlKey && !event.metaKey) {
      showWheelHint();
      return;
    }

    // Col modificatore premuto la rotella e' nostra, SEMPRE: anche quando la
    // scala e' gia' al minimo o al massimo e lo zoom non puo' cambiare.
    //
    // Qui prima si usciva senza preventDefault in quel caso, per non lasciare
    // una zona morta sopra la mappa. Ma la zona morta non era l'esito: il
    // browser raccoglieva l'evento e ingrandiva LA PAGINA. E poiche' la mappa
    // parte a MIN_SCALE, capitava al primo tentativo di rimpicciolire che
    // chiunque facesse — il gesto piu' naturale su una mappa gia' tutta
    // visibile. Chi ha il modificatore premuto sta chiedendo di zoomare
    // questa mappa: "non succede nulla" e' una risposta corretta a
    // "rimpicciolisci un mondo gia' intero", ingrandire il sito non lo e'.
    // Per scorrere la pagina basta lasciare il modificatore: quel ramo, qui
    // sopra, non e' cambiato.
    event.preventDefault();

    // Fattore proporzionale allo spostamento reale, non un valore fisso: un
    // fattore fisso applicato a ogni evento e' innocuo con la rotella di un
    // mouse (uno scatto = un evento con deltaY grande), ma sul trackpad il
    // pinch genera decine di eventi al secondo con deltaY minimo, e ognuno
    // moltiplicava comunque per lo stesso 1,12 fisso: in un quarto di secondo
    // si esauriva tutta l'escursione di zoom.
    const factor = wheelZoomFactor(normalizeWheelDeltaY(event));

    // Nessun clamp esplicito: ci pensa setView con clampView, che e' l'unico
    // posto in cui i limiti di scala vivono.
    setView({ k: view.k * factor, x: view.x, y: view.y });
  }, { passive: false });

  // --- pan e pinch ----------------------------------------------------------

  canvas.addEventListener('pointerdown', (event) => {
    // Il paese sotto il puntatore va letto ADESSO, non al click. Poche righe
    // piu' sotto il canvas cattura il puntatore (setPointerCapture) per non
    // perderlo se il trascinamento esce dal riquadro: da quel momento il
    // pointerup viene consegnato al canvas, e il click sintetico che il
    // browser ne ricava ha come bersaglio il canvas invece del poligono. Il
    // listener 'click' leggeva `data-iso` da quel bersaglio e non trovava
    // nulla: nessun paese risultava cliccabile, ne' i poligoni ne' i pallini
    // dei micro-stati. Era il motivo per cui l'unica strada verso il factbook
    // restava la CTA del tooltip.
    pressedIso = isoFrom(event) || null;
    pressedPointerType = event.pointerType || 'mouse';

    // I bottoni +/- stanno dentro il canvas: senza questa uscita il loro
    // pointerdown risale al listener del canvas e arma un trascinamento,
    // spostando la mappa invece di limitarsi a zoomare. `.dataset` e non
    // `.closest()`: i bottoni non hanno figli, quindi l'event.target e' gia'
    // il bottone stesso quando esiste l'attributo — `.closest()` cercherebbe
    // un antenato che non serve trovare.
    if (event.target.dataset && event.target.dataset.factbookMapZoom) {
      return;
    }

    // Stessa ragione dei bottoni +/- qui sopra, stessa correzione gia'
    // applicata li' (commit 9114314e): il tooltip sta dentro al canvas,
    // quindi il suo pointerdown risalirebbe fino a qui e armerebbe un
    // trascinamento. Un tremolio di 1-2px mentre si clicca verrebbe scambiato
    // per pan e la navigazione non partirebbe piu'. Tutto il box e non piu'
    // la sola CTA: da questa correzione l'intero tooltip e' cliccabile.
    if (tip.contains(event.target)) {
      return;
    }

    // Un pointerdown primario mentre risultano ancora puntatori attivi
    // significa che il gesto precedente non si e' chiuso correttamente: il
    // suo pointerup non e' mai arrivato (mouse rilasciato fuori dalla
    // finestra, perdita di focus della pagina...). Senza questa pulizia la
    // voce orfana resta per sempre in `activePointers`, il conteggio non
    // torna mai a zero, il reset qui sotto non scatta piu' e il tocco
    // successivo viene interpretato come secondo dito di un pinch (vedi
    // `activePointerCount() === 2` piu' sotto), rompendo il tap-per-navigare
    // fino al reload della pagina.
    if (event.isPrimary && activePointerCount() > 0) {
      Object.keys(activePointers).forEach((id) => { delete activePointers[id]; });
    }

    if (activePointerCount() === 0) {
      // Inizio di un gesto nuovo: nessun residuo del precedente deve
      // sopravvivere, in particolare suppressNextClick, altrimenti un
      // pointercancel senza click a valle lascerebbe il prossimo tap ignorato.
      dragDistance = 0;
      suppressNextClick = false;
      pinch = null;
    }

    activePointers[event.pointerId] = { x: event.clientX, y: event.clientY };
    canvas.classList.add('is-dragging');

    try {
      canvas.setPointerCapture(event.pointerId);
    } catch (error) {
      // Alcuni browser rifiutano la cattura se il puntatore non risulta piu'
      // "attivo" nell'istante fra evento e chiamata (tocchi molto rapidi, o
      // un secondo dito che il sistema tratta in modo particolare). La
      // cattura è solo una garanzia in piu' per non perdere il puntatore se
      // esce dal riquadro: senza try/catch, un suo fallimento abortirebbe
      // l'intero handler PRIMA di inizializzare pan o pinch qui sotto.
    }

    if (activePointerCount() === 1) {
      dragFrom = { x: event.clientX, y: event.clientY };
    } else if (activePointerCount() === 2) {
      // Il pan a un dito lascia il posto al pinch: la distanza e la scala
      // attuali sono il riferimento da cui calcolare il fattore di zoom.
      dragFrom = null;
      const [p1, p2] = activePointerList();
      pinch = {
        distance: distanceBetween(p1, p2),
        k: view.k,
        midX: (p1.x + p2.x) / 2,
        midY: (p1.y + p2.y) / 2,
      };
    }
  });

  canvas.addEventListener('pointermove', (event) => {
    if (!(event.pointerId in activePointers)) {
      return;
    }

    activePointers[event.pointerId] = { x: event.clientX, y: event.clientY };

    if (activePointerCount() >= 2) {
      const [p1, p2] = activePointerList();

      if (pinch) {
        const distance = distanceBetween(p1, p2);
        const factor = distance / pinch.distance;
        const midX = (p1.x + p2.x) / 2;
        const midY = (p1.y + p2.y) / 2;

        zoomAroundPoint(midX, midY, pinch.k * factor);

        // In modalita' cooperativa le due dita sono l'unico modo di spostare
        // la mappa in verticale (un dito in verticale scorre la pagina):
        // oltre a zoomare, la mappa segue lo spostamento del loro punto medio.
        if (cooperative) {
          const scale = 1 / (canvasUnit * view.k);

          setView({
            k: view.k,
            x: view.x + (midX - pinch.midX) * scale,
            y: view.y + (midY - pinch.midY) * scale,
          });
          pinch.midX = midX;
          pinch.midY = midY;
        }

        // Un pinch e' un gesto, non un tap: il click sintetico al rilascio
        // non deve navigare, esattamente come un pan.
        dragDistance = DRAG_THRESHOLD + 1;
      }

      return;
    }

    if (!dragFrom) {
      return;
    }

    const dx = event.clientX - dragFrom.x;
    const dy = event.clientY - dragFrom.y;
    const segment = Math.sqrt(dx * dx + dy * dy);

    if (segment >= PAN_NOISE_FLOOR) {
      dragDistance += segment;
    }

    const scale = 1 / (canvasUnit * view.k);

    setView({
      k: view.k,
      x: view.x + dx * scale,
      y: view.y + dy * scale,
    });

    dragFrom = { x: event.clientX, y: event.clientY };
  });

  function endDrag() {
    // Un trascinamento vero, anche breve, non deve tradursi in una
    // navigazione: un pan (o un pinch) che inizia e finisce dentro lo stesso
    // poligono altrimenti farebbe scattare il click e ci porterebbe sul
    // factbook del paese sotto il dito invece di limitarsi a spostare/zoomare
    // la mappa.
    suppressNextClick = dragDistance > DRAG_THRESHOLD;
    dragFrom = null;
    dragDistance = 0;
    pinch = null;
    canvas.classList.remove('is-dragging');
  }

  function releasePointer(event) {
    if (!(event.pointerId in activePointers)) {
      return;
    }

    delete activePointers[event.pointerId];

    if (activePointerCount() === 0) {
      endDrag();
    } else if (activePointerCount() === 1) {
      // Da pinch a pan: il dito rimasto riprende il trascinamento dalla sua
      // posizione attuale, non da quella (ormai vecchia) salvata prima del
      // pinch, altrimenti la mappa scatterebbe al prossimo movimento.
      const [remaining] = activePointerList();
      dragFrom = { x: remaining.x, y: remaining.y };
      pinch = null;
    }
  }

  canvas.addEventListener('pointerup', releasePointer);
  canvas.addEventListener('pointercancel', (event) => {
    // Un dito che il browser si riprende per scorrere la pagina: e' il
    // momento di dire come si muove la mappa, come fa la rotella senza Ctrl.
    if (cooperative && event.pointerType === 'touch' && activePointerCount() === 1) {
      showWheelHint('Use two fingers to move the map');
    }

    releasePointer(event);
  });

  if (cooperative) {
    // Con `touch-action: pan-y` il browser potrebbe trattare anche due dita
    // che si muovono in verticale come uno scorrimento della pagina, e
    // cancellare il pinch. Le due dita sono della mappa: lo scorrimento si
    // blocca qui, finche' il touchmove e' ancora annullabile.
    canvas.addEventListener('touchmove', (event) => {
      if (event.touches.length >= 2 && event.cancelable) {
        event.preventDefault();
      }
    }, { passive: false });
  }

  // --- selezione ----------------------------------------------------------

  if (canHover) {
    canvas.addEventListener('pointerover', (event) => {
      // Il puntatore e' entrato nel tooltip (box, ponte sullo stacco o CTA):
      // il paese mostrato resta quello, non si tocca nulla. Prima qui usciva
      // solo la CTA e il resto del box, essendo trasparente agli eventi,
      // lasciava passare l'hover al paese sottostante: partendo da un
      // micro-stato come Andorra il tooltip diventava quello della Francia
      // dopo pochi pixel, e la CTA di Andorra spariva da sotto il mouse.
      // `tip.contains` e non un controllo sulla classe: cosi' vale anche per
      // i nodi interni (nome, lingua) senza doverli elencare.
      if (tip.contains(event.target)) {
        return;
      }

      const iso = isoFrom(event);

      if (iso) {
        showTip(iso);
      } else if (!dragFrom) {
        hideTip();
      }
    });

    // Un paese puo' toccare il bordo del riquadro: uscendo da li' il mouse
    // lascia il canvas senza attraversare un'area "vuota" che triggerebbe
    // pointerover, e il tooltip resterebbe visibile senza motivo.
    canvas.addEventListener('pointerleave', () => {
      hideTip();
    });
  }

  // La ✕ e' l'uscita dichiarata dal tooltip, e serve soprattutto col dito: col
  // mouse basta spostarsi (pointerleave qui sopra), ma un dito che si alza non
  // produce nessun "via di qui". Il suo click risale al canvas come tutti gli
  // altri, e li' il controllo `tip.contains` in navigateFromEvent impedisce che
  // valga anche come tocco sulla mappa: chiudere non deve riaprire.
  tipClose.addEventListener('click', () => {
    hideTip();
  });

  /**
   * Apre il factbook di `country` rispettando il gesto: Cmd/Ctrl/Maiusc o
   * tasto centrale sono la richiesta esplicita di una scheda nuova, e
   * dirottare quella corrente vorrebbe dire buttare via lo zoom e il filtro
   * che l'utente ha impostato per arrivare fin li'. I poligoni sono <path>,
   * non link: senza questo controllo il browser non ha modo di onorare il
   * modificatore da solo. Il tooltip invece e' un <a> vero e non passa di
   * qui: quei gesti li gestisce il browser, meglio di come potremmo noi.
   */
  function openCountry(country, event) {
    const wantsNewTab = event && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (wantsNewTab) {
      window.open(country.url, '_blank', 'noopener');
      return;
    }

    window.location.href = country.url;
  }

  function navigateFromEvent(event) {
    if (suppressNextClick) {
      suppressNextClick = false;
      return;
    }

    // Il tooltip sta dentro al canvas: il suo click risale fin qui. E' un <a>
    // vero e la navigazione la fa gia' il browser — proseguire significherebbe
    // aprire una seconda volta, per giunta sull'ultimo `pressedIso`, che non e'
    // detto sia il paese mostrato adesso nel box. Stesso controllo, e stessa
    // ragione, del `tip.contains` in pointerover.
    if (tip.contains(event.target)) {
      return;
    }

    // `pressedIso` come ripiego: sotto cattura del puntatore il bersaglio del
    // click e' il canvas e `isoFrom()` torna a mani vuote (vedi pointerdown).
    // Prima si prova comunque il bersaglio, che e' quello giusto quando la
    // cattura non e' attiva o il browser non la applica al click.
    const iso = isoFrom(event) || pressedIso;
    const country = iso ? byIso[iso] : null;
    // L'evento 'click' e' esso stesso un PointerEvent nei browser correnti e
    // porta con se' il gesto che l'ha prodotto: quando c'e', e' la verita' piu'
    // fresca e va preferita. `pressedPointerType` resta il ripiego per i click
    // che pointerType non ce l'hanno (un MouseEvent sintetico, un click
    // generato da script) e per i browser che non lo espongono qui: e' un
    // valore memorizzato, quindi potenzialmente vecchio di un gesto.
    const byFinger = (event.pointerType || pressedPointerType) === 'touch';

    if (!country) {
      // Tocco a vuoto: mare aperto, o un paese senza factbook. Col dito e'
      // il gesto naturale per chiudere il tooltip — non c'e' un "uscire col
      // puntatore" che lo faccia da solo. Col mouse invece il tooltip lo
      // governa gia' l'hover (pointerover/pointerleave qui sopra) e chiuderlo
      // anche al click lo farebbe sparire da sotto un puntatore che gli e'
      // ancora sopra, per riaprirlo al primo pixel di movimento.
      if (byFinger) {
        hideTip();
      }

      return;
    }

    if (byFinger) {
      // Primo tocco: mostra il paese invece di portarcisi. Senza tooltip
      // intermedio il dito non riceve nessuna conferma di che cosa ha
      // colpito — su un poligono piccolo, o vicino a un confine, si finisce
      // sul factbook sbagliato senza aver mai visto il nome di quello giusto.
      // La navigazione resta a un tocco di distanza: il box e' un link, e da
      // qui non ci si passa (vedi il `tip.contains` in cima).
      showTip(iso);

      return;
    }

    openCountry(country, event);
  }

  canvas.addEventListener('click', navigateFromEvent);

  // Il tasto centrale non emette 'click' ma 'auxclick'. Solo il bottone 1: il
  // 2 e' il tasto destro, che deve continuare ad aprire il menu contestuale.
  canvas.addEventListener('auxclick', (event) => {
    if (event.button !== 1) {
      return;
    }

    // Senza questo, Windows e Linux avviano lo scorrimento automatico col
    // tasto centrale sopra la mappa mentre la scheda si apre.
    event.preventDefault();
    navigateFromEvent(event);
  });

  // --- ricerca ------------------------------------------------------------

  function currentFilters() {
    return {
      continent: select ? select.value : '',
      query: search ? search.value : '',
    };
  }

  /**
   * Filtra la lista leggendo i data attribute delle voci, non il payload della
   * mappa. È deliberato: la lista contiene TUTTI i factbook pubblicati, mentre
   * il payload contiene solo quelli con un codice paese. Filtrando sul payload,
   * un factbook privo di codice sparirebbe dalla lista al primo filtro e non
   * tornerebbe più — esattamente il contenuto che questa lista deve proteggere.
   */
  function renderList() {
    if (!list) {
      return;
    }

    const filters = currentFilters();
    let visibleGroups = 0;

    Array.from(list.querySelectorAll('.factbook-list__group')).forEach((group) => {
      const continent = group.dataset.continent || '';
      let visible = 0;

      Array.from(group.querySelectorAll('li')).forEach((item) => {
        const aliases = (item.dataset.aliases || '').split('|').filter(Boolean);
        const shown = matchesFilters({ name: item.dataset.name, continent, aliases }, filters);

        item.hidden = !shown;

        if (shown) {
          visible += 1;
        }
      });

      group.hidden = visible === 0;

      if (visible > 0) {
        visibleGroups += 1;
      }

      const count = group.querySelector('[data-count]');

      if (count) {
        count.textContent = String(visible);
      }
    });

    // Nessun gruppo superstite: senza il messaggio resterebbe il solo titolo
    // sopra una fascia bianca, e chi ha appena digitato non saprebbe se ha
    // sbagliato la parola o se la pagina si e' rotta. Il filtro continente da
    // solo non ci arriva mai (ogni voce del menu ha almeno un factbook): ci si
    // arriva digitando, o incrociando una ricerca con un continente che non la
    // contiene.
    if (empty) {
      empty.hidden = visibleGroups > 0;
    }
  }

  if (select) {
    // Il dropdown disegnato da noi si costruisce sopra questo select e vi
    // scrive sopra, poi fa scattare 'change': questo listener non sa nulla del
    // componente e continua a funzionare identico a prima.
    initContinentDropdown(select);

    select.addEventListener('change', () => {
      const bounds = payload.continents[select.value];
      // Il rettangolo dell'artefatto copre i soli poligoni: allargarlo sulle
      // ancore dei membri e' cio' che tiene dentro al riquadro anche i
      // micro-stati, che un poligono non ce l'hanno (vedi expandBounds).
      const framed = bounds
        ? expandBounds(bounds, anchorsByContinent[select.value], world.width)
        : null;

      setView(framed ? viewForBounds(framed, world, DEFAULT_PADDING, canvasVisible) : RESET_VIEW);
      hideTip();
      renderList();
    });
  }

  if (search) {
    search.addEventListener('input', () => {
      renderList();

      const continent = currentFilters().continent;
      const match = findBestMatch(filterCountries(payload.countries, { continent }), search.value);
      const anchor = match ? anchorFor(match.iso) : null;

      if (!anchor) {
        // Campo svuotato (o senza corrispondenza) e nessun filtro continente
        // attivo: senza questo reset la mappa resterebbe inquadrata
        // sull'ultimo paese trovato col tooltip ancora aperto, con nessun modo
        // per chi cancella il testo di tornare alla vista d'insieme. Se un
        // continente e' selezionato la vista resta la sua (impostata dal
        // listener di `select`), non torna al mondo intero.
        if (!search.value.trim() && !continent) {
          setView(RESET_VIEW);
          hideTip();
        }

        return;
      }

      // Inquadra un'area attorno al paese invece di saturare lo zoom: a 6x su
      // un micro-stato la geometria attorno si vede spigolosa.
      setView(viewForBounds(
        [anchor[0] - 72, anchor[1] - 50, anchor[0] + 72, anchor[1] + 50],
        world,
        DEFAULT_PADDING,
        canvasVisible,
      ));
      showTip(match.iso);
    });

    search.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') {
        return;
      }

      // Specifica riga 238: "Invio... porta alla pagina". Il paese
      // attualmente evidenziato e' quello che l'input sopra ha appena
      // zoomato e aperto nel tooltip (`activeIso`), non un "primo risultato"
      // implicito: senza un paese evidenziato Invio non naviga da nessuna
      // parte, cosi' come cliccare sulla mappa fuori da un paese non naviga.
      const country = activeIso ? byIso[activeIso] : null;

      if (country) {
        window.location.href = country.url;
      }
    });
  }

  window.addEventListener('resize', () => {
    measureCanvas();
    // Il box puo' andare a capo diversamente su un riquadro piu' stretto: i
    // rientri di placeTip() si calcolano sull'ingombro nuovo, non su quello
    // misurato prima del ridimensionamento.
    measureTip();
    placeTip();
  });

  measureTip();
  render();

  return { setView, showTip, hideTip };
}

// Ogni mappa della pagina si inizializza per conto suo (initFactbookMap cerca
// la prima [data-factbook-map] dentro lo scope che riceve).
Array.from(document.querySelectorAll('[data-factbook-map]')).forEach((node) => {
  initFactbookMap(node.parentNode);
});
